import React from "react";
import StatCard from "../components/StatCard";
import { api } from "../../api/api";
import ReceiptLogo from "../../Assets/logo-receipt.png";
import QRCode from "qrcode";
function n(v) {
  const num = Number(v);
  return Number.isFinite(num) ? num : 0;
}
function money(v) {
  const num = n(v);
  return `$${num.toFixed(2)}`;
}
function pad2(x) {
  return String(x).padStart(2, "0");
}
function formatDateTime(d) {
  const dd = new Date(d);
  return `${dd.getFullYear()}-${pad2(dd.getMonth() + 1)}-${pad2(dd.getDate())} ${pad2(dd.getHours())}:${pad2(
    dd.getMinutes()
  )}`;
}

const API_BASE =
  (process.env.REACT_APP_API_BASE || "https://nodeapi.sts-lb.net").replace(/\/$/, "");

function getToken() {
  return (
    localStorage.getItem("admin_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("sts_admin_token") ||
    ""
  );
}

async function fetchItemsDirect() {
  const token = getToken();
  if (!token) throw new Error("Missing token. Please login again.");

  const res = await fetch(`${API_BASE}/items`, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed (${res.status})`;
    throw new Error(msg);
  }

  return Array.isArray(data) ? data : [];
}

async function checkoutDirect(cartList) {
  const token = getToken();
  if (!token) throw new Error("Missing token. Please login again.");

  const payload = {
    items: cartList.map((row) => ({
      item_id: row.item.id,
      qty: n(row.qty),
    })),
  };

  const res = await fetch(`${API_BASE}/sales/checkout`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Checkout failed (${res.status})`;
    throw new Error(msg);
  }

  return data || {};
}

/* =========================
   Receipt Modal (Printable)
   ========================= */
function ReceiptModal({ open, onClose, receipt, logoSrc }) {
  const receiptRef = React.useRef(null);
  const [qrDataUrl, setQrDataUrl] = React.useState("");

  // NEW: customer fields (local only)
  const [customerName, setCustomerName] = React.useState("");
  const [customerPhone, setCustomerPhone] = React.useState("");

  // reset fields when opening a new receipt (optional behavior)
  React.useEffect(() => {
    if (!open) return;
    setCustomerName(receipt?.customerName || "");
    setCustomerPhone(receipt?.customerPhone || "");
  }, [open, receipt?.customerName, receipt?.customerPhone]);

  React.useEffect(() => {
    if (!open) return;

    const handle = (e) => {
      if (e.key === "Escape") onClose?.();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        handlePrint();
      }
    };

    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);

  React.useEffect(() => {
    let alive = true;
    const url = receipt?.website || "https://www.sts-lb.net";

    (async () => {
      try {
        const data = await QRCode.toDataURL(url, {
          errorCorrectionLevel: "M",
          margin: 1,
          width: 170,
        });
        if (alive) setQrDataUrl(data);
      } catch {
        if (alive) setQrDataUrl("");
      }
    })();

    return () => {
      alive = false;
    };
  }, [receipt?.website]);

  if (!open) return null;

  const r = receipt || {};
  const lines = Array.isArray(r.lines) ? r.lines : [];

  const totalQty = lines.reduce((sum, ln) => sum + (Number(ln.qty) || 0), 0);

  const dtBeirut = new Date(r.date || Date.now());
  const dtStr = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Beirut",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(dtBeirut);

  const money = (v) => {
    const num = Number(v);
    return `$${(Number.isFinite(num) ? num : 0).toFixed(2)}`;
  };

  function handlePrint() {
    const html = receiptRef.current?.innerHTML || "";
    const w = window.open("", "_blank", "width=520,height=760");
    if (!w) return;

    w.document.open();
    w.document.write(`
      <html>
        <head>
          <title>Smart Tech Solutions — Receipt</title>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <style>
            :root{
              --ink:#0b1220;
              --muted:#536073;
              --line:#e7ebf2;
              --soft:#f7f9fc;
              --brand:#2563eb;
            }
              @page{
              size:auto;
              margin: 10mm;
              }

            *{box-sizing:border-box}
            body{
              margin:0;
              padding:0px;
              font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Noto Sans";
              color:var(--ink);
              background:white;
            }
            .paper{
              width:100%;
              max-width: none;
              margin: 0;
              border: 1px solid var(--line);
              border-radius: 16px;
              overflow: hidden;
              box-shadow: 0 10px 28px rgba(11,18,32,0.08);
            }
            .pad{padding:16px 16px}
            .top{
              background: linear-gradient(135deg, rgba(37,99,235,0.10), rgba(37,99,235,0.02));
              border-bottom: 1px solid var(--line);
            }
            .brand{
              display:flex;
              align-items:center;
              justify-content:space-between;
              gap:12px;
            }
            .brand-left{
              display:flex; align-items:center; gap:10px;
              min-width: 0;
            }
            .logo{
              width:52px;height:52px;
              max-width:52px; max-height:52px;
              object-fit:contain;
              border-radius: 12px;
              background: white;
              border: 1px solid var(--line);
              padding: 6px;
              flex: 0 0 42px;
            }
            .title{
              font-weight: 800;
              letter-spacing: -0.02em;
              font-size: 16px;
              line-height: 1.1;
              margin:0;
              white-space:nowrap;
              overflow:hidden;
              text-overflow:ellipsis;
            }
            .subtitle{
              margin: 4px 0 0;
              font-size: 11px;
              color: var(--muted);
            }
            .meta{
              margin-top: 12px;
              display:grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
            }
            .pill{
              background: rgba(255,255,255,0.85);
              border: 1px solid var(--line);
              border-radius: 12px;
              padding: 10px;
            }
            .k{
              font-size: 10px;
              color: var(--muted);
              text-transform: uppercase;
              letter-spacing: 0.18em;
            }
            .v{
              margin-top: 4px;
              font-size: 12px;
              font-weight: 700;
              word-break: break-word;
            }
            table{width:100%;border-collapse:collapse}
            th{
              text-align:left;
              font-size:10px;
              color: var(--muted);
              letter-spacing: 0.16em;
              text-transform: uppercase;
              padding: 10px 0;
              border-bottom: 1px solid var(--line);
            }
            td{
              padding: 12px 0;
              border-bottom: 1px solid var(--line);
              vertical-align: top;
              font-size: 12px;
            }
            .qty,.amt{text-align:right}
            .name{
              font-weight: 700;
              margin:0;
            }
            .desc{
              margin-top: 4px;
              color: var(--muted);
              font-size: 11px;
              line-height: 1.25;
              white-space: pre-wrap;
            }
            .totalBox{
              background: var(--soft);
              border: 1px solid var(--line);
              border-radius: 14px;
              padding: 12px;
              display:flex;
              align-items:flex-start;
              justify-content:space-between;
              gap: 10px;
            }
            .totalLabel{
              font-size:10px;
              color: var(--muted);
              letter-spacing: 0.16em;
              text-transform: uppercase;
            }
            .grand{
              font-size: 18px;
              font-weight: 900;
              letter-spacing: -0.02em;
              margin-top: 6px;
            }
            .qrWrap{
              display:flex;
              align-items:center;
              justify-content:space-between;
              gap: 12px;
              margin-top: 12px;
            }
            .qrCard{
              border: 1px solid var(--line);
              border-radius: 14px;
              padding: 10px;
              background: white;
              display:flex;
              align-items:center;
              gap: 10px;
            }
            .qrImg{
              width: 74px;
              height: 74px;
              object-fit: contain;
              border-radius: 10px;
              border: 1px solid var(--line);
              padding: 6px;
              background: white;
            }
            .qrText{min-width:0}
            .qrText .big{
              font-weight: 800;
              font-size: 12px;
              margin:0;
            }
            .qrText .small{
              margin-top: 4px;
              font-size: 11px;
              color: var(--muted);
              word-break: break-all;
            }

            /* NEW: signature block */
            .signWrap{
              margin-top: 14px;
              border: 1px dashed var(--line);
              border-radius: 14px;
              padding: 12px;
              background: white;
            }
            .signTitle{
              font-size:10px;
              color: var(--muted);
              letter-spacing: 0.16em;
              text-transform: uppercase;
              margin:0 0 10px;
            }
            .signLine{
              height: 36px;
              border-bottom: 2px solid var(--ink);
              opacity: 0.9;
            }
            .signHint{
              margin-top: 8px;
              font-size: 11px;
              color: var(--muted);
            }

            .footer{
              text-align:center;
              color: var(--muted);
              font-size: 11px;
              padding: 14px 16px;
              background: white;
            }
            @media print{
              body{padding:0}
              .paper{box-shadow:none; border: none; width:100%; max-width:none; margin:0
                }
            }
          </style>
        </head>
        <body>
          ${html}
          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            }
          </script>
        </body>
      </html>
    `);
    w.document.close();
  }

  return (
    <div className="fixed inset-0 z-[100]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute inset-0 grid place-items-center p-4 md:p-6">
        <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#0b1220]/95 text-white shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between shrink-0">
            <div>
              <div className="font-semibold text-lg">Receipt</div>
              <div className="text-white/60 text-sm">Add customer details before printing</div>
            </div>
            <button
              onClick={onClose}
              className="h-10 w-10 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 text-white/80"
              title="Close"
            >
              ✕
            </button>
          </div>

          {/* Scrollable content */}
          <div className="p-6 overflow-y-auto flex-1 overscroll-contain space-y-4">
            {/* NEW: Customer input panel (not printed directly; values are printed inside receipt below) */}
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-4">
              <div className="text-white font-semibold">Customer Details</div>
              <div className="text-white/60 text-sm">Optional — will appear on the printed receipt</div>

              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/60 mb-1">Customer name</label>
                  <input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="e.g. Ahmad / Sarah"
                    className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 outline-none text-white placeholder:text-white/30 focus:border-white/20"
                  />
                </div>

                <div>
                  <label className="block text-xs text-white/60 mb-1">Phone number</label>
                  <input
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="e.g. 70 216 395"
                    className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 outline-none text-white placeholder:text-white/30 focus:border-white/20"
                  />
                </div>
              </div>
            </div>

            {/* Print target */}
            <div ref={receiptRef}>
              <div className="paper">
                <div className="top pad">
                  <div className="brand">
                    <div className="brand-left">
                      {logoSrc ? <img className="logo" src={logoSrc} alt="STS" /> : null}
                      <div style={{ minWidth: 0 }}>
                        <p className="title">Smart Tech Solutions</p>
                        <p className="subtitle">Sales Receipt</p>
                      </div>
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 10, letterSpacing: "0.18em", color: "#536073", textTransform: "uppercase" }}>
                        Invoice
                      </div>
                      <div style={{ fontWeight: 900, fontSize: 12 }}>{r.invoiceNo || "—"}</div>
                    </div>
                  </div>

                  <div className="meta">
                    <div className="pill">
                      <div className="k">Date & Time </div>
                      <div className="v">{dtStr}</div>
                    </div>
                    <div className="pill">
                      <div className="k">Total Quantity</div>
                      <div className="v">{totalQty}</div>
                    </div>
                  </div>

                  {/* NEW: customer printed meta */}
                  <div className="meta" style={{ marginTop: 10 }}>
                    <div className="pill">
                      <div className="k">Customer</div>
                      <div className="v">{customerName?.trim() ? customerName.trim() : "—"}</div>
                    </div>
                    <div className="pill">
                      <div className="k">Phone</div>
                      <div className="v">{customerPhone?.trim() ? customerPhone.trim() : "—"}</div>
                    </div>
                  </div>
                </div>

                <div className="pad">
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="qty">Qty</th>
                        <th className="amt">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((ln, i) => {
                        const qty = Number(ln.qty) || 0;
                        const sell = Number(ln.sell_price) || 0;
                        const lineTotal = sell * qty;

                        return (
                          <tr key={i}>
                            <td>
                              <div className="name">{ln.name || "—"}</div>
                              {ln.description ? <div className="desc">{ln.description}</div> : null}
                            </td>
                            <td className="qty">{qty}</td>
                            <td className="amt">{money(lineTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div style={{ height: 14 }} />

                  <div className="totalBox">
                    <div>
                      <div className="totalLabel">Grand Total</div>
                      <div className="grand">{money(r.total_sell || 0)}</div>
                    </div>
                    <div style={{ textAlign: "right", color: "#536073", fontSize: 11, lineHeight: 1.25 }}>
                      Thank you for your purchase.
                      <div>We appreciate your trust.</div>
                    </div>
                  </div>

                  <div className="qrWrap">
                    <div className="qrCard" style={{ flex: 1 }}>
                      {qrDataUrl ? <img className="qrImg" src={qrDataUrl} alt="QR" /> : null}
                      <div className="qrText">
                        <p className="big">Scan to visit our website</p>
                        <div className="small">{r.website || "https://www.sts-lb.net"}</div>
                      </div>
                    </div>
                  </div>

                  {/* NEW: signature area */}
                  <div className="signWrap">
                    <p className="signTitle">Customer Signature</p>
                    <div className="signLine"></div>
                    <div className="signHint">Sign here to confirm receipt of items.</div>
                  </div>
                </div>

                <div className="footer">Smart Tech Solutions • Websites • CCTV • Repairs • Support</div>
              </div>
            </div>
          </div>

          {/* Pinned actions */}
          <div className="px-6 pb-6 shrink-0">
            <div className="mt-2 flex gap-2">
              <button
                onClick={handlePrint}
                className="flex-1 py-3 rounded-2xl font-semibold border border-white/20 bg-white/10 hover:bg-white/15"
              >
                Print Receipt
              </button>
              <button
                onClick={onClose}
                className="px-5 py-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 text-white/80"
              >
                Close
              </button>
            </div>

            <div className="mt-3 text-xs text-white/50">Shortcut: Ctrl/Cmd + P inside the receipt.</div>
          </div>
        </div>
      </div>
    </div>
  );
}



/* =========================
   Dashboard (POS Cart + Receipt)
   ========================= */
export default function Dashboard() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");

  const [cart, setCart] = React.useState({});
  const [checkingOut, setCheckingOut] = React.useState(false);
  const [cartMsg, setCartMsg] = React.useState("");
  const [cartErr, setCartErr] = React.useState("");

  // receipt state
  const [receiptOpen, setReceiptOpen] = React.useState(false);
  const [receipt, setReceipt] = React.useState(null);

  const refresh = React.useCallback(async () => {
    setErr("");
    setLoading(true);

    try {
      const data = await api.listItems("");
      const arr = Array.isArray(data) ? data : [];
      if (arr.length === 0) {
        const direct = await fetchItemsDirect();
        setItems(direct);
      } else {
        setItems(arr);
      }
    } catch (e) {
      try {
        const direct = await fetchItemsDirect();
        setItems(direct);
      } catch (e2) {
        console.error(e2);
        setErr(e2?.message || "Failed to load items");
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const total = items.length;
  const totalQty = items.reduce((sum, it) => sum + n(it.quantity), 0);
  const totalValue = items.reduce((sum, it) => sum + n(it.quantity) * n(it.unit_cost), 0);

  const last5 = React.useMemo(() => {
    const arr = [...items];
    const hasDate = arr.some((x) => x.updated_at || x.created_at);
    if (hasDate) {
      arr.sort((a, b) => {
        const ad = new Date(a.updated_at || a.created_at || 0).getTime();
        const bd = new Date(b.updated_at || b.created_at || 0).getTime();
        return bd - ad;
      });
    }
    return arr.slice(0, 5);
  }, [items]);

  const cartList = React.useMemo(() => Object.values(cart), [cart]);

  const cartTotals = React.useMemo(() => {
    const totalCost = cartList.reduce((sum, row) => sum + n(row.item.unit_cost) * n(row.qty), 0);
    const totalSell = cartList.reduce((sum, row) => sum + n(row.item.sell_price) * n(row.qty), 0);
    const profit = totalSell - totalCost;
    const lines = cartList.reduce((sum, row) => sum + n(row.qty), 0);
    return { totalCost, totalSell, profit, lines };
  }, [cartList]);

  function addToCart(item) {
    setCartMsg("");
    setCartErr("");
    const stock = n(item.quantity);
    if (stock <= 0) return;

    setCart((prev) => {
      const current = prev[item.id]?.qty || 0;
      const next = Math.min(stock, n(current) + 1);
      return { ...prev, [item.id]: { item, qty: next } };
    });
  }

  function inc(id) {
    setCartMsg("");
    setCartErr("");
    setCart((prev) => {
      const row = prev[id];
      if (!row) return prev;
      const stockNow = n(items.find((x) => x.id === id)?.quantity ?? row.item.quantity);
      const next = Math.min(stockNow, n(row.qty) + 1);
      return { ...prev, [id]: { ...row, qty: next } };
    });
  }

  function dec(id) {
    setCartMsg("");
    setCartErr("");
    setCart((prev) => {
      const row = prev[id];
      if (!row) return prev;
      const next = n(row.qty) - 1;
      if (next <= 0) {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      }
      return { ...prev, [id]: { ...row, qty: next } };
    });
  }

  function removeFromCart(id) {
    setCartMsg("");
    setCartErr("");
    setCart((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  }

  async function checkout() {
    setCartMsg("");
    setCartErr("");

    if (cartList.length === 0) {
      setCartErr("Cart is empty.");
      return;
    }

    setCheckingOut(true);
    try {
      // prepare receipt lines from cart (POS-style)
      const receiptLines = cartList.map((row) => ({
        id: row.item.id,
        name: row.item.name,
        description: row.item.description || row.item.notes || "",
        qty: n(row.qty),
        sell_price: n(row.item.sell_price),
      }));

      const result = await checkoutDirect(cartList);

      // build receipt object
      const now = new Date();
      const receiptNo =
        result.receipt_no ||
        `STS-${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}-${Math.floor(
          1000 + Math.random() * 9000
        )}`;

      const r = {
        receiptNo,
        date: now.toISOString(),
        website: "https://www.sts-lb.net",
        lines: receiptLines,
        total_cost: result.total_cost ?? cartTotals.totalCost,
        total_sell: result.total_sell ?? cartTotals.totalSell,
        profit: result.profit ?? cartTotals.profit,
        total_lines: receiptLines.length,
      };

      setCart({});
      await refresh();

      setCartMsg(
        `Sale completed ✅  Sell: ${money(r.total_sell)} • Cost: ${money(r.total_cost)} • Profit: ${money(r.profit)}`
      );

      // open receipt modal
      setReceipt(r);
      setReceiptOpen(true);
    } catch (e) {
      console.error(e);
      setCartErr(e?.message || "Checkout failed");
    } finally {
      setCheckingOut(false);
    }
  }

  return (
    <div className="space-y-6">
      <ReceiptModal
        open={receiptOpen}
        receipt={receipt}
        onClose={() => setReceiptOpen(false)}
         logoSrc={ReceiptLogo}
      />

      <div className="grid md:grid-cols-3 gap-4">
        <StatCard title="Total Items" value={loading ? "…" : total} hint="All inventory products" icon="📦" />
        <StatCard title="Total Quantity" value={loading ? "…" : totalQty} hint="Sum of all stock" icon="🧮" />
        <StatCard
          title="Inventory Cost Value"
          value={loading ? "…" : money(totalValue)}
          hint="quantity × unit_cost"
          icon="💰"
        />
      </div>

      {err ? (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 text-red-100 px-4 py-3 text-sm flex items-center justify-between gap-3">
          <div>
            <div className="font-semibold">Could not load items</div>
            <div className="opacity-90">{err}</div>
          </div>
          <button
            onClick={refresh}
            className="px-4 py-2 rounded-2xl border border-white/20 bg-white/10 text-white hover:bg-white/15"
          >
            Retry
          </button>
        </div>
      ) : null}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Items */}
        <div className="lg:col-span-2 rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-white font-semibold">Recent Items</div>
              <div className="text-white/60 text-sm">Add products quickly to cart</div>
            </div>

            <button
              onClick={refresh}
              className="px-4 py-2 rounded-2xl border border-white/10 bg-white/5 text-white/80 hover:text-white hover:bg-white/10"
            >
              Refresh
            </button>
          </div>

          <div className="p-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-white/60 text-sm">
                  <th className="py-3 px-3">Name</th>
                  <th className="py-3 px-3">Barcode</th>
                  <th className="py-3 px-3">Qty</th>
                  <th className="py-3 px-3">Unit Cost</th>
                  <th className="py-3 px-3">Sell Price</th>
                  <th className="py-3 px-3 text-right">Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-t border-white/10">
                      <td className="py-4 px-3">
                        <div className="h-4 w-40 rounded bg-white/10 animate-pulse" />
                      </td>
                      <td className="py-4 px-3">
                        <div className="h-4 w-28 rounded bg-white/10 animate-pulse" />
                      </td>
                      <td className="py-4 px-3">
                        <div className="h-4 w-10 rounded bg-white/10 animate-pulse" />
                      </td>
                      <td className="py-4 px-3">
                        <div className="h-4 w-16 rounded bg-white/10 animate-pulse" />
                      </td>
                      <td className="py-4 px-3">
                        <div className="h-4 w-16 rounded bg-white/10 animate-pulse" />
                      </td>
                      <td className="py-4 px-3">
                        <div className="h-9 w-28 rounded-xl bg-white/10 animate-pulse ml-auto" />
                      </td>
                    </tr>
                  ))
                ) : last5.map((it) => {
                    const qty = n(it.quantity);
                    const disabled = qty <= 0;
                    return (
                      <tr key={it.id} className="border-t border-white/10 text-white/90">
                        <td className="py-3 px-3 font-medium">{it.name}</td>
                        <td className="py-3 px-3 text-white/70">{it.barcode || "-"}</td>
                        <td className="py-3 px-3">{qty}</td>
                        <td className="py-3 px-3">{money(it.unit_cost)}</td>
                        <td className="py-3 px-3">{money(it.sell_price)}</td>
                        <td className="py-3 px-3 text-right">
                          <button
                            onClick={() => addToCart(it)}
                            disabled={disabled}
                            className={[
                              "px-4 py-2 rounded-2xl border transition",
                              disabled
                                ? "border-white/10 bg-white/5 text-white/30 cursor-not-allowed"
                                : "border-white/20 bg-white/10 text-white hover:bg-white/15",
                            ].join(" ")}
                          >
                            + Add
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                {!loading && last5.length === 0 ? (
                  <tr>
                    <td className="py-6 px-3 text-white/60" colSpan={6}>
                      No items yet. Add your first one from the Items page.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        {/* Cart */}
        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-white font-semibold">Cart</div>
              <div className="text-white/60 text-sm">Checkout then print receipt</div>
            </div>
            <div className="text-white/60 text-sm">
              Items: <span className="text-white/90 font-semibold">{cartTotals.lines}</span>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {cartErr ? (
              <div className="rounded-2xl border border-red-500/30 bg-red-500/10 text-red-100 px-4 py-3 text-sm">
                {cartErr}
              </div>
            ) : null}
            {cartMsg ? (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-100 px-4 py-3 text-sm">
                {cartMsg}
              </div>
            ) : null}

            {cartList.length === 0 ? (
              <div className="text-white/60 text-sm">Cart is empty. Add items from Recent Items.</div>
            ) : (
              cartList.map((row) => {
                const item = row.item;
                const id = item.id;
                const qty = n(row.qty);
                const stockNow = n(items.find((x) => x.id === id)?.quantity ?? item.quantity);
                const canInc = qty < stockNow;

                return (
                  <div key={id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-white font-medium">{item.name}</div>
                        <div className="text-white/60 text-xs mt-1">
                          Stock: <span className="text-white/90">{stockNow}</span> • Cost:{" "}
                          <span className="text-white/90">{money(item.unit_cost)}</span> • Sell:{" "}
                          <span className="text-white/90">{money(item.sell_price)}</span>
                        </div>
                      </div>

                      <button
                        onClick={() => removeFromCart(id)}
                        className="text-white/60 hover:text-white text-sm"
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => dec(id)}
                          className="h-9 w-9 rounded-xl border border-white/10 bg-white/5 text-white hover:bg-white/10"
                        >
                          −
                        </button>
                        <div className="min-w-[44px] text-center text-white font-semibold">{qty}</div>
                        <button
                          onClick={() => inc(id)}
                          disabled={!canInc}
                          className={[
                            "h-9 w-9 rounded-xl border text-white transition",
                            canInc
                              ? "border-white/10 bg-white/5 hover:bg-white/10"
                              : "border-white/10 bg-white/5 text-white/30 cursor-not-allowed",
                          ].join(" ")}
                        >
                          +
                        </button>
                      </div>

                      <div className="text-right">
                        <div className="text-white/60 text-xs">Line total (Sell)</div>
                        <div className="text-white font-semibold">{money(n(item.sell_price) * qty)}</div>
                      </div>
                    </div>

                    {!canInc && stockNow > 0 ? (
                      <div className="mt-3 text-xs text-amber-200/90">Reached max stock.</div>
                    ) : null}
                  </div>
                );
              })
            )}

            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex justify-between text-white/80 text-sm">
                <span>Total Cost</span>
                <span className="text-white">{money(cartTotals.totalCost)}</span>
              </div>
              <div className="flex justify-between text-white/80 text-sm mt-2">
                <span>Total Sell</span>
                <span className="text-white">{money(cartTotals.totalSell)}</span>
              </div>
              <div className="flex justify-between text-white/80 text-sm mt-2 font-semibold">
                <span>Profit</span>
                <span className="text-white">{money(cartTotals.profit)}</span>
              </div>

              <button
                onClick={checkout}
                disabled={checkingOut || cartList.length === 0}
                className={[
                  "mt-4 w-full py-3 rounded-2xl font-semibold transition border",
                  checkingOut || cartList.length === 0
                    ? "border-white/10 bg-white/5 text-white/30 cursor-not-allowed"
                    : "border-white/20 bg-white/10 text-white hover:bg-white/15",
                ].join(" ")}
              >
                {checkingOut ? "Processing…" : "Checkout (Complete Sale)"}
              </button>

              <div className="mt-3 text-xs text-white/50">
                After checkout, a printable receipt will pop up.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
